import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MessageSquare, Calendar, Database, ArrowRight, Check, Bell, Sparkles, Clock } from 'lucide-react';
import Monitor from './Monitor';

// Helper to respect reduced motion
const useMotionConfig = () => {
  const shouldReduceMotion = useReducedMotion();
  return {
    shouldReduceMotion: !!shouldReduceMotion,
  };
};

// (1) Real-Time Orders & Scheduling
export const RealTimeOrdersAnimation = () => {
  const { shouldReduceMotion } = useMotionConfig();
  const [isHovered, setIsHovered] = React.useState(false);

  if (shouldReduceMotion) {
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <Monitor width="w-48" height="h-32">
          <div className="w-full h-full bg-slate-900 flex items-center justify-center">
            <Calendar className="w-16 h-16 text-slate-400" />
          </div>
        </Monitor>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Monitor width="w-48" height="h-32">
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <motion.div
            className="relative w-20 h-20 flex items-center justify-center"
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Chat bubble (fades out on entrance only) */}
            <motion.div
              initial={{ opacity: 1, scale: 1 }}
              whileInView={{ 
                opacity: [1, 0],
                scale: [1, 0.92]
              }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ 
                delay: 0.2,
                duration: 0.4,
                ease: 'easeInOut'
              }}
              className="absolute"
            >
              <MessageSquare className="w-12 h-12 text-slate-400" />
            </motion.div>

            {/* Calendar (fades in on entrance, then stays) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ 
                opacity: [0, 1],
                scale: [0.92, 1]
              }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ 
                delay: 0.2,
                duration: 0.4,
                ease: 'easeInOut'
              }}
              className="absolute"
            >
              <Calendar className="w-12 h-12 text-slate-400" />
            </motion.div>

            {/* Incoming WhatsApp message - loops continuously */}
            <motion.div
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0.8, 1, 1, 0.8],
                y: [-4, 0, 0, -4]
              }}
              transition={{
                delay: 0.6,
                duration: 1.2,
                repeat: Infinity,
                repeatDelay: 2,
                ease: 'easeOut'
              }}
              className="absolute -left-8 top-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-5 h-5 fill-emerald-500">
                <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
              </svg>
            </motion.div>

            {/* Calendar slot fill - loops continuously */}
            <motion.div
              initial={{ x: -8, opacity: 0 }}
              animate={{
                x: [-8, 0, -8],
                opacity: [0, 1, 0]
              }}
              transition={{
                delay: 1.2,
                duration: 2.5,
                repeat: Infinity,
                repeatDelay: 1.5,
                ease: 'easeInOut'
              }}
              className="absolute w-6 h-2.5 bg-blue-500/60 rounded-sm mt-1"
            />

            {/* Soft pulse - loops continuously */}
            <motion.div
              animate={{
                scale: [1, 1.04, 1]
              }}
              transition={{
                delay: 1.8,
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
              className="absolute inset-0"
            />

            {/* Expanding ring - loops continuously */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0.25 }}
              animate={{
                scale: [0.9, 1.25],
                opacity: [0.25, 0]
              }}
              transition={{
                delay: 1.8,
                duration: 2.5,
                repeat: Infinity,
                repeatDelay: 1,
                ease: 'easeOut'
              }}
              className="absolute inset-0 border-2 border-blue-400/30 rounded-full"
            />
          </motion.div>
        </div>
      </Monitor>
    </div>
  );
};

// (2) CRM Automatically Updated
export const CRMAutoUpdatedAnimation = () => {
  const { shouldReduceMotion } = useMotionConfig();
  const [isHovered, setIsHovered] = React.useState(false);

  if (shouldReduceMotion) {
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <Monitor width="w-48" height="h-32">
          <div className="w-full h-full bg-slate-900 flex items-center justify-center">
            <Database className="w-12 h-12 text-slate-400" />
          </div>
        </Monitor>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Monitor width="w-48" height="h-32">
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <motion.div
            className="relative w-full h-full flex items-center justify-center"
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Database icon - center */}
            <Database className="w-16 h-16 text-slate-400" />

            {/* WhatsApp icon: top-left → center → top-left (bidirectional, always visible) */}
            <motion.div
              animate={{
                x: [-20, 0, 0, -20],
                y: [-20, 0, 0, -20],
                scale: [0.8, 1, 1, 0.8]
              }}
              transition={{
                delay: 1,
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                times: [0, 0.2, 0.8, 1]
              }}
              className="absolute opacity-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-5 h-5 fill-emerald-500">
                <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
              </svg>
            </motion.div>

            {/* Synkro icon: center → top-right → center (bidirectional, always visible) */}
            <motion.div
              animate={{
                x: [0, 0, 20, 0, 0],
                y: [0, 0, -20, 0, 0],
                scale: [0.8, 0.8, 1, 1, 0.8]
              }}
              transition={{
                delay: 1,
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                times: [0, 0.2, 0.5, 0.8, 1]
              }}
              className="absolute opacity-100"
            >
              <img src="/synkro.png" alt="Synkro" className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </div>
      </Monitor>
    </div>
  );
};

// (3) Automated Follow-Ups & Reminders
export const AutomatedFollowUpsAnimation = () => {
  const { shouldReduceMotion } = useMotionConfig();
  const [isHovered, setIsHovered] = React.useState(false);

  if (shouldReduceMotion) {
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <Monitor width="w-48" height="h-32">
          <div className="w-full h-full bg-slate-900 flex items-center justify-center">
            <Bell className="w-16 h-16 text-slate-400" />
          </div>
        </Monitor>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Monitor width="w-48" height="h-32">
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <motion.div
            className="relative w-20 h-20 flex items-center justify-center"
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Bell - gentle swing loops continuously */}
            <motion.div
              animate={{
                rotate: [0, -8, 6, -4, 0]
              }}
              transition={{
                delay: 1,
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
              style={{ transformOrigin: 'top center' }}
            >
              <Bell className="w-14 h-14 text-slate-400" />
            </motion.div>

            {/* Notification dot - loops continuously */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{
                opacity: [0.25, 1, 1, 0.25],
                scale: [0.6, 1.0, 1.0, 0.6]
              }}
              transition={{
                delay: 1.2,
                duration: 1.5,
                repeat: Infinity,
                repeatDelay: 1,
                ease: 'easeInOut',
                times: [0, 0.3, 0.7, 1]
              }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
            />

            {/* WhatsApp message sending out - loops continuously */}
            <motion.div
              animate={{
                x: [0, 12, 0],
                y: [0, -10, 0],
                opacity: [0, 1, 0]
              }}
              transition={{
                delay: 1.8,
                duration: 1.8,
                repeat: Infinity,
                repeatDelay: 2,
                ease: 'easeOut'
              }}
              className="absolute"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-4 h-4 fill-emerald-500">
                <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </Monitor>
    </div>
  );
};

// (4) Personalized Replies
export const PersonalizedRepliesAnimation = () => {
  const { shouldReduceMotion } = useMotionConfig();
  const [isHovered, setIsHovered] = React.useState(false);

  if (shouldReduceMotion) {
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <Monitor width="w-48" height="h-32">
          <div className="w-full h-full bg-slate-900 flex items-center justify-center">
            <MessageSquare className="w-16 h-16 text-slate-400" />
          </div>
        </Monitor>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Monitor width="w-48" height="h-32">
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <motion.div
            className="relative w-20 h-20 flex items-center justify-center"
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Bubble - subtle pulse loops continuously */}
            <motion.div
              animate={{
                scale: [1, 1.03, 1]
              }}
              transition={{
                delay: 1,
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            >
              <MessageSquare className="w-14 h-14 text-slate-400" />
            </motion.div>

            {/* Typing dots - loops continuously */}
            <div className="absolute -bottom-2 flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    opacity: [0.2, 1, 0.2]
                  }}
                  transition={{
                    delay: 1.2 + i * 0.15,
                    duration: 1.2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="w-1.5 h-1.5 bg-slate-500 rounded-full"
                />
              ))}
            </div>

            {/* Spark - loops continuously */}
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: 0 }}
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [0.9, 1.0, 0.9],
                rotate: [0, 12, 0]
              }}
              transition={{
                delay: 1.5,
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1,
                ease: 'easeInOut'
              }}
              className="absolute -top-1 -right-1"
            >
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </motion.div>

            {/* Text lines morph - loops continuously (smaller and centered) */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
              {[0, 1].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    width: ['40%', '50%', '45%', '40%']
                  }}
                  transition={{
                    delay: 1.8 + i * 0.3,
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="h-0.5 bg-slate-500/60 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        </div>
      </Monitor>
    </div>
  );
};

// (5) 24/7 Always-On
export const AlwaysOnAnimation = () => {
  const { shouldReduceMotion } = useMotionConfig();
  const [isHovered, setIsHovered] = React.useState(false);

  if (shouldReduceMotion) {
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <Monitor width="w-48" height="h-32">
          <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
            <Clock className="w-16 h-16 text-slate-400" />
            <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full" />
          </div>
        </Monitor>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Monitor width="w-48" height="h-32">
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <motion.div
            className="relative w-20 h-20 flex items-center justify-center"
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Clock */}
            <div className="relative">
              <Clock className="w-16 h-16 text-slate-400" />
              
              {/* Clock hand rotation - loops continuously */}
              <motion.div
                animate={{
                  rotate: [0, 360]
                }}
                transition={{
                  delay: 1,
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear'
                }}
                style={{ transformOrigin: 'center center' }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-0.5 h-6 bg-slate-600 origin-top mt-2" />
              </motion.div>

              {/* Power dot - breathing effect loops continuously */}
              <motion.div
                animate={{
                  opacity: [1, 0.75, 1]
                }}
                transition={{
                  delay: 1.5,
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"
              />
            </div>

            {/* Soft glow pulse - loops continuously */}
            <motion.div
              animate={{
                scale: [0.9, 1.25],
                opacity: [0.18, 0]
              }}
              transition={{
                delay: 1,
                duration: 3,
                repeat: Infinity,
                repeatDelay: 1,
                ease: 'easeOut'
              }}
              className="absolute inset-0 border-2 border-blue-400/30 rounded-full"
            />
          </motion.div>
        </div>
      </Monitor>
    </div>
  );
};
